import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'

import {
  HomeTabType,
  SlotPositionItemType,
  SlotPositionType,
} from 'features/MultiCategory/@types/homeTab'
import { HOME_TAB_STATUS_NAME } from 'features/MultiCategory/constant/homeTab'
import {
  deleteHomeTab as deleteHomeTabService,
  getHomeTabList,
  HomeTabListItemResponseType,
  updateHomeTabSortPosition as updateHomeTabSortPositionService,
} from 'features/MultiCategory/services/homeTab'
import { formatUtcToWib } from 'features/MultiCategory/utils/date'
import {
  SLICE_NAME,
  setIsLoading,
  setIsSlotPositionsLoading,
  setList,
  setSlotPositions,
} from './slice'

const EMPTY_VALUE = '-'

/* API timestamps are UTC; the admin shows WIB. */
const formatDateTime = (date?: string) => formatUtcToWib(date, 'DD MMM YYYY, HH:mm') || EMPTY_VALUE

/* Schedules stay empty when unset so the UI can hide them. */
const formatSchedule = (date?: string, format = 'DD MMM YYYY, HH:mm') =>
  formatUtcToWib(date, format) || ''

const mapHomeTab = (item: HomeTabListItemResponseType): HomeTabType => ({
  id: String(item.id),
  slot: item.slot ? String(item.slot) : EMPTY_VALUE,
  position: item.position ? String(item.position) : EMPTY_VALUE,
  name: item.name || EMPTY_VALUE,
  type: item.homepage_tabbing_type_name || EMPTY_VALUE,
  segment: item.targeting_type_name || EMPTY_VALUE,
  /* `status` is the source of truth; `status_name` only mirrors it. */
  status: HOME_TAB_STATUS_NAME[item.status] || 'Draft',
  scheduleStart: formatSchedule(item.start_date_time),
  scheduleEnd: formatSchedule(item.end_date_time),
  createdBy: item.created_by || EMPTY_VALUE,
  lastUpdatedBy: item.updated_by || EMPTY_VALUE,
  lastUpdatedAt: formatDateTime(item.updated_at),
})

/** One page is enough to lay out every slot; the drawer does not paginate. */
const SLOT_POSITION_PAGE_SIZE = 100

const toSlotPositionItem = (item: HomeTabListItemResponseType): SlotPositionItemType => ({
  id: String(item.id),
  name: item.name || EMPTY_VALUE,
  type: item.homepage_tabbing_type_name || EMPTY_VALUE,
  segment: item.targeting_type_name || EMPTY_VALUE,
  status: HOME_TAB_STATUS_NAME[item.status] || 'Draft',
  periodStart: formatSchedule(item.start_date_time, 'DD/MM/YY HH:mm'),
  periodEnd: formatSchedule(item.end_date_time, 'DD/MM/YY HH:mm'),
})

/** Groups the tabs by slot, ordered by slot then position. */
const toSlotPositions = (items: HomeTabListItemResponseType[]): SlotPositionType[] => {
  /* A tab without a slot or a position has no place in the layout yet. */
  const placed = items.filter((item) => Number(item.slot) > 0 && Number(item.position) > 0)
  const slots = placed
    .map((item) => Number(item.slot))
    .filter((slot, index, list) => list.indexOf(slot) === index)
    .sort((left, right) => left - right)

  return slots.map((slot) => ({
    slot: `Slot ${slot}`,
    positions: placed
      .filter((item) => Number(item.slot) === slot)
      .sort((left, right) => Number(left.position) - Number(right.position))
      .map(toSlotPositionItem),
  }))
}

export const fetchHomeTabSlotPositions = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabSlotPositions`,
  async (_: void, { dispatch }) => {
    dispatch(setIsSlotPositionsLoading(true))

    try {
      const {
        data: { data },
      } = await getHomeTabList({ pageIndex: 1, pageSize: SLOT_POSITION_PAGE_SIZE })

      dispatch(setSlotPositions(toSlotPositions(data || [])))
    } catch (error) {
      callErrorMsg(error)
      dispatch(setSlotPositions([]))
    } finally {
      dispatch(setIsSlotPositionsLoading(false))
    }
  },
)

export const fetchMulticategoryHomepageList = createAsyncThunk(
  `${SLICE_NAME}/fetchMulticategoryHomepageList`,
  async (_: void, { dispatch, getState }) => {
    const {
      multiCategoryHomeTab: { query, pagination },
    } = getState() as StoreStateType

    dispatch(setIsLoading(true))

    try {
      const {
        data: { data, pagination: paginationResponse, show_multicategory_warning },
      } = await getHomeTabList({
        /* API page index is 1-based: both 0 and 1 return the first page. */
        pageIndex: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        name: query.name || undefined,
        status: query.status ? Number(query.status) : undefined,
        homepageTabbingTypeId: query.type ? Number(query.type) : undefined,
      })

      dispatch(
        setList({
          list: (data || []).map(mapHomeTab),
          numberOfElements: paginationResponse?.number_of_elements || 0,
          showMulticategoryWarning: Boolean(show_multicategory_warning),
        }),
      )
    } catch (error) {
      callErrorMsg(error)
      dispatch(setList({ list: [], numberOfElements: 0, showMulticategoryWarning: false }))
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const deleteHomeTab = createAsyncThunk(
  `${SLICE_NAME}/deleteHomeTab`,
  async (id: string, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      await deleteHomeTabService(id)

      toastSuccess('Home tab berhasil dihapus')

      /* The list owns the page it is on, so it refetches instead of splicing. */
      dispatch(fetchMulticategoryHomepageList())

      return id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export type UpdateHomeTabSortPositionParamsType = {
  slot: number
  /** Tab ids in their new order; positions are derived from the index. */
  orderedIds: string[]
}

export const updateHomeTabSortPosition = createAsyncThunk(
  `${SLICE_NAME}/updateHomeTabSortPosition`,
  async (
    { slot, orderedIds }: UpdateHomeTabSortPositionParamsType,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsSlotPositionsLoading(true))

    try {
      await updateHomeTabSortPositionService({
        slot,
        items: orderedIds.map((id, index) => ({ id: Number(id), position: index + 1 })),
      })

      toastSuccess('Posisi tab berhasil disimpan')

      /* Both views read `position`, so both refetch. */
      dispatch(fetchHomeTabSlotPositions())
      dispatch(fetchMulticategoryHomepageList())

      return slot
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSlotPositionsLoading(false))
    }
  },
)
